import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faBell,
  faUser,
  faTachometerAlt,
  faUsers,
  faUserPlus,
  faSignOutAlt,
  faArchive,
  faHome,
  faCog,
  faChartBar,
  faClipboardCheck,
  faChevronDown,
  faChevronRight,
  faMap,
  faUserCheck,
  faList,
  faClipboardList,
  faFileText,
  faMoneyBillWave,
  faPlus,
  faExclamationTriangle,
  faHistory,
  faCheckCircle,
  faBan,
  faTasks,
  faBullhorn,
  faPhone,
  faChartLine,
  faChartPie,
  faCalendarAlt,
  faPause,
  faPersonWalking,
  faSearch,
  faRoute,
  faMapLocationDot,
  faRobot,
  faFileUpload
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from '../components/NotificationBell';

const SideBar = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleMobileMenu = () => {
    console.log('Mobile menu toggled:', !isMobileMenuOpen);
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleDropdown = (dropdownName) => {
    setOpenDropdowns(prev => {
      // Close all dropdowns first, then open only the clicked one if it wasn't open
      const wasOpen = prev[dropdownName];
      const newState = {};
      // Close all dropdowns
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });
      // Open only the clicked dropdown if it wasn't already open
      if (!wasOpen) {
        newState[dropdownName] = true;
      }
      return newState;
    });
  };

  const handleLogout = async () => {
    await logout();
  };

  // Navigation items based on user role
  const getNavigationItems = () => {
    const baseItems = [
      {
        name: 'Tableau de Bord',
        icon: faTachometerAlt,
        path: '/dashboard',
        show: true
      }
    ];

    // Project Lifecycle Module (CE/CES, CTS, AG/AGS, resp_admin, resp_controle)
    if (['charge_etude', 'charge_etude_superieur', 'chef_terrain_superieur',
         'administrateur_general', 'administrateur_general_superieur', 'resp_admin',
         'resp_controle'].includes(user?.role)) {
      baseItems.push({
        name: 'Projets',
        icon: faClipboardList,
        type: 'dropdown',
        subItems: [
          { name: 'En cours', icon: faList, path: '/projects', show: true },
          { name: 'Nouveau Projet', icon: faPlus, path: '/projects/create', show: true },
          { name: 'Archives', icon: faArchive, path: '/projects/archive', show: true },
        ],
        show: true,
      });
    }

    // Payment Management Dropdown for chef_terrain_superieur (right after Dashboard)
    if (user?.role === 'chef_terrain_superieur') {
      baseItems.push({
        name: 'Paiements',
        icon: faMoneyBillWave,
        show: true,
        type: 'dropdown',
        subItems: [
          {
            name: 'Paiements Non Effectués',
            icon: faExclamationTriangle,
            path: '/payments/unpaid',
            show: true
          },
          {
            name: 'Lots de Paiement',
            icon: faList,
            path: '/payments',
            show: true
          },
          {
            name: 'Nouveau Lot',
            icon: faPlus,
            path: '/payments/create',
            show: true
          },
          {
            name: 'Lots Archivés',
            icon: faArchive,
            path: '/payments/archived',
            show: true
          },
          {
            name: 'Paiements Prestataires',
            icon: faUsers,
            path: '/payments/prestataires',
            show: true
          }
        ]
      });
    }

    // Role-specific items
    // Responsables Management Dropdown - For admin_it (before Paiements)
    if (user?.role === 'admin_it') {
      baseItems.push({
        name: 'Responsables',
        icon: faUsers,
        type: 'dropdown',
        subItems: [
          {
            name: 'Liste',
            icon: faUsers,
            path: '/users'
          },
          {
            name: 'Nouveau Responsable',
            icon: faUserPlus,
            path: '/users/create'
          },
          {
            name: 'Responsables Archivés',
            icon: faArchive,
            path: '/users/archived'
          },
          {
            name: 'Audit Responsables',
            icon: faClipboardList,
            path: '/audit'
          }
        ],
        show: true
      });

      // Supervisors Management Dropdown for admin_it
      baseItems.push({
        name: 'Prestataires',
        icon: faUsers,
        type: 'dropdown',
        subItems: [
          {
            name: 'Liste',
            icon: faUsers,
            path: '/supervisors'
          },
          {
            name: 'Nouveau',
            icon: faUserPlus,
            path: '/supervisors/create'
          },
          {
            name: 'Carte',
            icon: faMap,
            path: '/supervisors-map'
          },
          {
            name: 'Archive',
            icon: faArchive,
            path: '/supervisors-archived'
          }
        ],
        show: true
      });

      // Controllers Management Dropdown for admin_it
      baseItems.push({
        name: 'Contrôleurs',
        icon: faUsers,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Liste',
            icon: faList,
            path: '/controllers',
            show: true
          },
          {
            name: 'Nouveau',
            icon: faUserPlus,
            path: '/controllers/create',
            show: true
          },
          {
            name: 'Archivés',
            icon: faArchive,
            path: '/controllers/archived',
            show: true
          }
        ]
      });

      // Interviewers Management Dropdown for admin_it
      baseItems.push({
        name: 'Enquêteurs',
        icon: faUsers,
        type: 'dropdown',
        subItems: [
          {
            name: 'Liste',
            icon: faUsers,
            path: '/interviewers',
            show: true
          },
          {
            name: 'Nouvel Enquêteur',
            icon: faUserPlus,
            path: '/interviewers/create',
            show: true
          },
          {
            name: 'Enquêteurs Archivés',
            icon: faArchive,
            path: '/interviewers/archived',
            show: true
          }
        ],
        show: true
      });
    }


    // Payments Management Dropdown - Only for administrateur_general
    if (user?.role === 'administrateur_general' || user?.role === 'administrateur_general_superieur') {
      baseItems.push({
        name: 'Paiements',
        icon: faMoneyBillWave,
        type: 'dropdown',
        subItems: [
          {
            name: 'Vérification Paiements',
            icon: faUserCheck,
            path: '/payments/verification'
          },
          {
            name: 'Historique Paiements',
            icon: faHistory,
            path: '/payments/history'
          },
          {
            name: 'Paiements Non Effectués',
            icon: faExclamationTriangle,
            path: '/payments/unpaid'
          },
          {
            name: 'Audit Paiements',
            icon: faClipboardList,
            path: '/payments/audit'
          }
        ],
        show: true
      });

      // Responsables Management Dropdown - For administrateur_general (after Paiements)
      baseItems.push({
        name: 'Responsables',
        icon: faUsers,
        type: 'dropdown',
        subItems: [
          {
            name: 'Liste',
            icon: faUsers,
            path: '/users'
          },
          {
            name: 'Nouveau Responsable',
            icon: faUserPlus,
            path: '/users/create'
          },
          {
            name: 'Responsables Archivés',
            icon: faArchive,
            path: '/users/archived'
          },
          {
            name: 'Audit Responsables',
            icon: faClipboardList,
            path: '/audit'
          }
        ],
        show: true
      });

      // CATI Operations Section for administrateur_general
      const catiSubItems = [
        {
          name: 'Enquêteurs CATI',
          icon: faUsers,
          path: '/cati/interviewers'
        },
        {
          name: 'Productivité',
          icon: faChartLine,
          path: '/cati/productivity'
        },
        {
          name: 'Statistiques Productivité',
          icon: faChartBar,
          path: '/cati/productivity/stats'
        }
      ];
      if (user?.role === 'administrateur_general_superieur') {
        catiSubItems.unshift({
          name: 'Affectations CATI',
          icon: faFileText,
          path: '/cati/interview-uploads'
        });
      }
      baseItems.push({
        name: 'Opérations CATI',
        icon: faPhone,
        type: 'dropdown',
        subItems: catiSubItems,
        show: true
      });

      // Operations F2F Section for administrateur_general
      const f2fSubItems = [];
      f2fSubItems.push(
        {
          name: 'Quotas',
          icon: faChartPie,
          path: '/quotas'
        }
      );
      f2fSubItems.push(
        {
          name: 'Productivité',
          icon: faChartBar,
          path: '/f2f/productivity'
        },
        {
          name: 'Carte Prestataires',
          icon: faMap,
          path: '/supervisors-map'
        },
        {
          name: 'Frais de Déplacement',
          icon: faRoute,
          path: '/f2f/frais-deplacement'
        }
      );
      baseItems.push({
        name: 'Operations F2F',
        icon: faPersonWalking,
        type: 'dropdown',
        subItems: f2fSubItems,
        show: true
      });

      // Control Module for administrateur_general (read-only) - after Operations F2F
      baseItems.push({
        name: 'Contrôle Qualité',
        icon: faClipboardCheck,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Contrôleurs',
            icon: faUsers,
            path: '/controllers',
            show: true
          },
          {
            name: 'Contrôles Complétés',
            icon: faCheckCircle,
            path: '/control/completed',
            show: true
          },
          {
            name: 'Statistiques Contrôle',
            icon: faChartBar,
            path: '/control/stats',
            show: true
          },
          {
            name: 'Activité Contrôleurs',
            icon: faChartLine,
            path: '/control/controller-stats',
            show: true
          }
        ]
      });
      // Formations & Briefs for administrateur_general + administrateur_general_superieur
      baseItems.push({
        name: 'Formations & Briefs',
        icon: faBullhorn,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Liste',
            icon: faList,
            path: '/formations',
            show: true
          },
          {
            name: 'Nouvelle',
            icon: faPlus,
            path: '/formations/create',
            show: true
          }
        ]
      });

    }

    if (user?.role === 'chef_terrain_superieur') {
      // Enquêtes (CATI + F2F) Section for chef_terrain_superieur
      baseItems.push({
        name: 'Enquêtes',
        icon: faFileText,
        type: 'dropdown',
        subItems: [
          {
            name: 'Nouvelle',
            icon: faPlus,
            path: '/cati/surveys/create'
          },
          {
            name: 'Liste',
            icon: faList,
            path: '/cati/surveys'
          }
        ],
        show: true
      });

      // Control Module for chef_terrain_superieur (read-only)
      baseItems.push({
        name: 'Contrôle Qualité',
        icon: faClipboardCheck,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Lots Qualité',
            icon: faClipboardList,
            path: '/control/quality-batches',
            show: true
          },
          {
            name: 'Contrôle Complet',
            icon: faClipboardCheck,
            path: '/control/full-control',
            show: true
          },
          {
            name: 'Contrôles Complétés',
            icon: faCheckCircle,
            path: '/control/completed',
            show: true
          },
          {
            name: 'Statistiques Contrôle',
            icon: faChartBar,
            path: '/control/stats',
            show: true
          },
          {
            name: 'Contrôleurs',
            icon: faUsers,
            path: '/controllers',
            show: true
          },
          {
            name: 'Activité Contrôleurs',
            icon: faChartLine,
            path: '/control/controller-stats',
            show: true
          }
        ]
      });

      // Operations F2F Section for chef_terrain_superieur
      baseItems.push({
        name: 'Operations F2F',
        icon: faPersonWalking,
        type: 'dropdown',
        subItems: [
          {
            name: 'Quotas',
            icon: faChartPie,
            path: '/quotas',
            show: true
          },
          {
            name: 'Carte des adresses',
            icon: faMapLocationDot,
            path: '/quotas/addresses-map',
            show: true
          },
          {
            name: 'Statistiques productivité',
            icon: faChartBar,
            path: '/f2f/productivity',
            show: true
          },
          {
            name: 'Frais de Déplacement',
            icon: faRoute,
            path: '/f2f/frais-deplacement',
            show: true
          }
        ],
        show: true
      });

      // CATI Operations Section for chef_terrain_superieur
      baseItems.push({
        name: 'Opérations CATI',
        icon: faPhone,
        type: 'dropdown',
        subItems: [
          {
            name: 'Enquêteurs CATI',
            icon: faUsers,
            path: '/cati/interviewers'
          },
          {
            name: 'Statistiques Productivité',
            icon: faChartBar,
            path: '/cati/productivity/stats'
          }
        ],
        show: true
      });

      // Formations & Briefs for chef_terrain_superieur
      baseItems.push({
        name: 'Formations & Briefs',
        icon: faBullhorn,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Liste',
            icon: faList,
            path: '/formations',
            show: true
          },
          {
            name: 'Nouvelle',
            icon: faPlus,
            path: '/formations/create',
            show: true
          }
        ]
      });

      // Responsables Management Dropdown (chef_terrain, resp_controle, resp_cati)
      baseItems.push({
        name: 'Responsables',
        icon: faUsers,
        type: 'dropdown',
        subItems: [
          {
            name: 'Liste',
            icon: faUsers,
            path: '/responsables'
          },
          {
            name: 'Nouveau',
            icon: faUserPlus,
            path: '/responsables/create'
          },
          {
            name: 'Archive',
            icon: faArchive,
            path: '/responsables/archived'
          }
        ],
        show: true
      });

      // Superviseurs
      baseItems.push({
        name: 'Prestataires',
        icon: faUsers,
        type: 'dropdown',
        subItems: [
          {
            name: 'Liste',
            icon: faUsers,
            path: '/supervisors'
          },
          {
            name: 'Carte',
            icon: faMap,
            path: '/supervisors-map'
          }
        ],
        show: true
      });

    } else if (user?.role === 'chef_terrain') {
      // Payment Section Validation for chef_terrain
      baseItems.push({
        name: 'Paiements',
        icon: faMoneyBillWave,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Validation Sections',
            icon: faCheckCircle,
            path: '/payments/sections',
            show: true
          },
          {
            name: 'Paiements Non Effectués',
            icon: faExclamationTriangle,
            path: '/payments/unpaid',
            show: true
          },
          {
            name: 'Paiements Precedents',
            icon: faHistory,
            path: '/payments/history',
            show: true
          },
          {
            name: 'Paiements Prestataires',
            icon: faUsers,
            path: '/payments/prestataires',
            show: true
          }
        ]
      });

      // Enquêtes F2F Section for chef_terrain (right after Tableau de Bord)
      baseItems.push({
        name: 'Enquêtes',
        icon: faFileText,
        type: 'dropdown',
        subItems: [
          {
            name: 'Nouvelle',
            icon: faPlus,
            path: '/cati/surveys/create'
          },
          {
            name: 'Liste',
            icon: faList,
            path: '/cati/surveys'
          }
        ],
        show: true
      });

      // Opérations F2F Dropdown for chef_terrain
      baseItems.push({
        name: 'Opérations F2F',
        icon: faPersonWalking,
        type: 'dropdown',
        subItems: [
          {
            name: 'Quotas',
            icon: faChartPie,
            path: '/quotas',
            show: true
          },
          {
            name: 'Carte des adresses',
            icon: faMapLocationDot,
            path: '/quotas/addresses-map',
            show: true
          },
          {
            name: 'Productivité F2F',
            icon: faChartBar,
            path: '/f2f/productivity',
            show: true
          },
          {
            name: 'Frais de Déplacement',
            icon: faRoute,
            path: '/f2f/frais-deplacement',
            show: true
          }
        ],
        show: true
      });

      // Control Module for chef_terrain (read-only, results & stats only)
      baseItems.push({
        name: 'Contrôle Qualité',
        icon: faClipboardCheck,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Contrôles Complétés',
            icon: faCheckCircle,
            path: '/control/completed',
            show: true
          },
          {
            name: 'Statistiques Contrôle',
            icon: faChartBar,
            path: '/control/stats',
            show: true
          }
        ]
      });

      // Formations & Briefs for chef_terrain
      baseItems.push({
        name: 'Formations & Briefs',
        icon: faBullhorn,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Liste',
            icon: faList,
            path: '/formations',
            show: true
          },
          {
            name: 'Nouvelle',
            icon: faPlus,
            path: '/formations/create',
            show: true
          }
        ]
      });

      // Supervisors Management Dropdown for regular chef_terrain
      baseItems.push({
        name: 'Prestataires',
        icon: faUsers,
        type: 'dropdown',
        subItems: [
          {
            name: 'Liste',
            icon: faUsers,
            path: '/supervisors'
          },
          {
            name: 'Nouveau',
            icon: faUserPlus,
            path: '/supervisors/create'
          },
          {
            name: 'Carte',
            icon: faMap,
            path: '/supervisors-map'
          },
          {
            name: 'Archive',
            icon: faArchive,
            path: '/supervisors-archived'
          }
        ],
        show: true
      });

      // Interviewers Management Dropdown for chef_terrain (F2F only) - placed after Supervisors
      baseItems.push({
        name: 'Enquêteurs',
        icon: faUsers,
        type: 'dropdown',
        subItems: [
          {
            name: 'Liste',
            icon: faUsers,
            path: '/interviewers',
            show: true
          },
          {
            name: 'Nouvel Enquêteur',
            icon: faUserPlus,
            path: '/interviewers/create',
            show: true
          },
          {
            name: 'Enquêteurs Archivés',
            icon: faArchive,
            path: '/interviewers/archived',
            show: true
          }
        ],
        show: true
      });

    }

    if (user?.role === 'resp_controle') {
      // Payment Section Validation for resp_controle
      baseItems.push({
        name: 'Paiements',
        icon: faMoneyBillWave,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Validation Sections',
            icon: faCheckCircle,
            path: '/payments/sections',
            show: true
          },
          {
            name: 'Paiements Non Effectués',
            icon: faExclamationTriangle,
            path: '/payments/unpaid',
            show: true
          },
          {
            name: 'Paiements Precedents',
            icon: faHistory,
            path: '/payments/history',
            show: true
          }
        ]
      });

      baseItems.push({
        name: 'Contrôleurs',
        icon: faUsers,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Liste',
            icon: faList,
            path: '/controllers',
            show: true
          },
          {
            name: 'Nouveau',
            icon: faUserPlus,
            path: '/controllers/create',
            show: true
          },
          {
            name: 'Présence',
            icon: faUserCheck,
            path: '/control/attendance',
            show: true
          },
          {
            name: 'Activité Contrôleurs',
            icon: faChartLine,
            path: '/control/controller-stats',
            show: true
          },
          {
            name: 'Archivés',
            icon: faArchive,
            path: '/controllers/archived',
            show: true
          },
          {
            name: 'Audit',
            icon: faClipboardList,
            path: '/audit',
            show: true
          }
        ]
      });

      // Control Module for resp_controle
      baseItems.push({
        name: 'Contrôle Qualité',
        icon: faClipboardCheck,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Sélection Enquêtes',
            icon: faList,
            path: '/control/surveys',
            show: true
          },
          {
            name: 'Alertes',
            icon: faExclamationTriangle,
            path: '/control/alerts',
            show: true
          },
          {
            name: 'Lots Qualité',
            icon: faClipboardList,
            path: '/control/quality-batches',
            show: true
          },
          {
            name: 'Contrôle Complet',
            icon: faClipboardCheck,
            path: '/control/full-control',
            show: true
          },
          {
            name: 'Contrôles Complétés',
            icon: faCheckCircle,
            path: '/control/completed',
            show: true
          },
          {
            name: 'Statistiques Contrôle',
            icon: faChartBar,
            path: '/control/stats',
            show: true
          },
          {
            name: 'Contrôles avant paie',
            icon: faMoneyBillWave,
            path: '/control/f2f-payment-preview',
            show: true
          }
        ]
      });

      // Mon Espace Contrôle for resp_controle (direct control)
      baseItems.push({
        name: 'Mon Espace Contrôle',
        icon: faClipboardCheck,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Contrôler une Soumission',
            icon: faClipboardCheck,
            path: '/resp-controle/workspace',
            show: true
          },
          {
            name: 'Mes Contrôles',
            icon: faHistory,
            path: '/resp-controle/my-checks',
            show: true
          },
          {
            name: 'Mis en Attente',
            icon: faPause,
            path: '/resp-controle/on-hold',
            show: true
          }
        ]
      });

      // Contrôle IA section for resp_controle
      baseItems.push({
        name: 'Contrôle IA',
        icon: faRobot,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Uploader',
            icon: faFileUpload,
            path: '/resp-controle/ai/upload',
            show: true
          },
          {
            name: 'Contrôles IA effectués',
            icon: faRobot,
            path: '/resp-controle/ai/checks',
            show: true
          }
        ]
      });

      // Enquêtes CATI Section for resp_controle
      baseItems.push({
        name: 'Enquêtes',
        icon: faFileText,
        type: 'dropdown',
        subItems: [
          {
            name: 'Nouvelle',
            icon: faPlus,
            path: '/cati/surveys/create'
          },
          {
            name: 'Liste',
            icon: faList,
            path: '/cati/surveys'
          }
        ],
        show: true
      });

      // CATI Operations Section for resp_controle
      baseItems.push({
        name: 'Opérations CATI',
        icon: faPhone,
        type: 'dropdown',
        subItems: [
          {
            name: 'Enquêteurs CATI',
            icon: faUsers,
            path: '/cati/interviewers'
          },
          {
            name: 'Statistiques Productivité',
            icon: faChartBar,
            path: '/cati/productivity/stats'
          }
        ],
        show: true
      });

      // F2F Operations Section for resp_controle (read-only)
      baseItems.push({
        name: 'Opérations F2F',
        icon: faPersonWalking,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Prestataires',
            icon: faUsers,
            path: '/supervisors',
            show: true
          },
          {
            name: 'Enquêteurs',
            icon: faUsers,
            path: '/interviewers',
            show: true
          },
          {
            name: 'Productivité F2F',
            icon: faChartBar,
            path: '/f2f/productivity',
            show: true
          }
        ]
      });

      // Formations & Briefs for resp_controle
      baseItems.push({
        name: 'Formations & Briefs',
        icon: faBullhorn,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Liste',
            icon: faList,
            path: '/formations',
            show: true
          },
          {
            name: 'Nouvelle',
            icon: faPlus,
            path: '/formations/create',
            show: true
          }
        ]
      });

    }

    if (user?.role === 'controleur') {
      // Mes Paiements - direct item after dashboard
      baseItems.push({
        name: 'Mes Paiements',
        icon: faMoneyBillWave,
        path: '/controle/my-payments',
        show: true
      });

      // Control Module for controleur
      baseItems.push({
        name: 'Contrôle',
        icon: faClipboardCheck,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Espace de Contrôle',
            icon: faClipboardCheck,
            path: '/control/workspace',
            show: true
          },
          {
            name: 'Rechercher & Contrôler',
            icon: faSearch,
            path: '/control/search',
            show: true
          },
          {
            name: 'Mis en Attente',
            icon: faPause,
            path: '/control/on-hold',
            show: true
          },
          {
            name: 'Mes Contrôles',
            icon: faHistory,
            path: '/control/my-checks',
            show: true
          }
        ]
      });
    }

    if (user?.role === 'charge_etude_superieur') {
      // CATI Operations Section for charge_etude_superieur
      baseItems.push({
        name: 'Opérations CATI',
        icon: faPhone,
        type: 'dropdown',
        subItems: [
          {
            name: 'Affectations CATI',
            icon: faFileText,
            path: '/cati/interview-uploads'
          },
          {
            name: 'Enquêteurs CATI',
            icon: faUsers,
            path: '/cati/interviewers'
          },
          {
            name: 'Statistiques Productivité',
            icon: faChartBar,
            path: '/cati/productivity/stats'
          }
        ],
        show: true
      });

      // Operations F2F Section for charge_etude_superieur
      baseItems.push({
        name: 'Operations F2F',
        icon: faPersonWalking,
        type: 'dropdown',
        subItems: [
          {
            name: 'Quotas',
            icon: faChartPie,
            path: '/quotas'
          },
          {
            name: 'Prestataires',
            icon: faUsers,
            path: '/supervisors'
          },
          {
            name: 'Carte',
            icon: faMap,
            path: '/supervisors-map'
          },
          {
            name: 'Productivité',
            icon: faChartBar,
            path: '/f2f/productivity'
          }
        ],
        show: true
      });

      // Control Module for charge_etude_superieur (read-only)
      baseItems.push({
        name: 'Contrôle Qualité',
        icon: faClipboardCheck,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Lots Qualité',
            icon: faClipboardList,
            path: '/control/quality-batches',
            show: true
          },
          {
            name: 'Contrôle Complet',
            icon: faClipboardCheck,
            path: '/control/full-control',
            show: true
          },
          {
            name: 'Contrôles Complétés',
            icon: faCheckCircle,
            path: '/control/completed',
            show: true
          },
          {
            name: 'Statistiques Contrôle',
            icon: faChartBar,
            path: '/control/stats',
            show: true
          },
          {
            name: 'Contrôleurs',
            icon: faUsers,
            path: '/controllers',
            show: true
          }
        ]
      });

      // Formations for charge_etude_superieur
      baseItems.push({
        name: 'Formations & Briefs',
        icon: faBullhorn,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Liste',
            icon: faList,
            path: '/formations',
            show: true
          },
          {
            name: 'Nouvelle',
            icon: faPlus,
            path: '/formations/create',
            show: true
          }
        ]
      });

      baseItems.push({
        name: 'Chargés d\'Études',
        icon: faUsers,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Liste',
            icon: faList,
            path: '/charge-etudes',
            show: true
          },
          {
            name: 'Nouveau',
            icon: faUserPlus,
            path: '/charge-etudes/create',
            show: true
          },
          {
            name: 'Audit',
            icon: faClipboardList,
            path: '/audit',
            show: true
          },
          {
            name: 'Archivés',
            icon: faArchive,
            path: '/users/archived',
            show: true
          }
        ]
      });

      // Enquêtes Section for charge_etude_superieur
      baseItems.push({
        name: 'Enquêtes',
        icon: faFileText,
        type: 'dropdown',
        subItems: [
          {
            name: 'Nouvelle',
            icon: faPlus,
            path: '/cati/surveys/create'
          },
          {
            name: 'Liste',
            icon: faList,
            path: '/cati/surveys'
          }
        ],
        show: true
      });

    }

    if (user?.role === 'charge_etude') {
      // CATI Operations Section for charge_etude
      baseItems.push({
        name: 'Opérations CATI',
        icon: faPhone,
        type: 'dropdown',
        subItems: [
          {
            name: 'Affectations CATI',
            icon: faFileText,
            path: '/cati/interview-uploads'
          },
          {
            name: 'Enquêteurs CATI',
            icon: faUsers,
            path: '/cati/interviewers'
          },
          {
            name: 'Statistiques Productivité',
            icon: faChartBar,
            path: '/cati/productivity/stats'
          }
        ],
        show: true
      });

      // Operations F2F Section for charge_etude
      baseItems.push({
        name: 'Operations F2F',
        icon: faPersonWalking,
        type: 'dropdown',
        subItems: [
          {
            name: 'Quotas',
            icon: faChartPie,
            path: '/quotas'
          },
          {
            name: 'Prestataires',
            icon: faUsers,
            path: '/supervisors'
          },
          {
            name: 'Carte',
            icon: faMap,
            path: '/supervisors-map'
          },
          {
            name: 'Productivité',
            icon: faChartBar,
            path: '/f2f/productivity'
          }
        ],
        show: true
      });

      // Control Module for charge_etude (read-only)
      baseItems.push({
        name: 'Contrôle Qualité',
        icon: faClipboardCheck,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Lots Qualité',
            icon: faClipboardList,
            path: '/control/quality-batches',
            show: true
          },
          {
            name: 'Contrôle Complet',
            icon: faClipboardCheck,
            path: '/control/full-control',
            show: true
          },
          {
            name: 'Contrôles Complétés',
            icon: faCheckCircle,
            path: '/control/completed',
            show: true
          },
          {
            name: 'Statistiques Contrôle',
            icon: faChartBar,
            path: '/control/stats',
            show: true
          },
          {
            name: 'Contrôleurs',
            icon: faUsers,
            path: '/controllers',
            show: true
          }
        ]
      });

      // Formations for charge_etude
      baseItems.push({
        name: 'Formations & Briefs',
        icon: faBullhorn,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Liste',
            icon: faList,
            path: '/formations',
            show: true
          },
          {
            name: 'Nouvelle',
            icon: faPlus,
            path: '/formations/create',
            show: true
          }
        ]
      });

      // Enquêtes Section for charge_etude
      baseItems.push({
        name: 'Enquêtes',
        icon: faFileText,
        type: 'dropdown',
        subItems: [
          {
            name: 'Nouvelle',
            icon: faPlus,
            path: '/cati/surveys/create'
          },
          {
            name: 'Liste',
            icon: faList,
            path: '/cati/surveys'
          }
        ],
        show: true
      });

    }

    if (user?.role === 'resp_admin') {
      baseItems.push({
        name: 'Vérification et Contrats',
        icon: faClipboardCheck,
        path: '/interviewers/verification',
        show: true
      });
      
      // Payment Management Dropdown
      baseItems.push({
        name: 'Paiements',
        icon: faMoneyBillWave,
        show: true,
        type: 'dropdown',
        subItems: [
          {
            name: 'Paiements Non Effectués',
            icon: faExclamationTriangle,
            path: '/payments/unpaid',
            show: true
          },
          {
            name: 'Lots de Paiement',
            icon: faList,
            path: '/payments',
            show: true
          }
        ]
      });
    }

    if (user?.role === 'resp_cati') {
      // Payment Section Validation for resp_cati
      baseItems.push({
        name: 'Paiements',
        icon: faMoneyBillWave,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Validation Sections',
            icon: faCheckCircle,
            path: '/payments/sections',
            show: true
          },
          {
            name: 'Paiements Non Effectués',
            icon: faExclamationTriangle,
            path: '/payments/unpaid',
            show: true
          },
          {
            name: 'Paiements Precedents',
            icon: faHistory,
            path: '/payments/history',
            show: true
          }
        ]
      });

      // CATI Operations Section
      baseItems.push({
        name: 'Opérations CATI',
        icon: faPhone,
        type: 'dropdown',
        subItems: [
          {
            name: 'Affectations CATI',
            icon: faFileText,
            path: '/cati/interview-uploads'
          },
          {
            name: 'Productivité',
            icon: faChartLine,
            path: '/cati/productivity'
          },
          {
            name: 'Statistiques Productivité',
            icon: faChartBar,
            path: '/cati/productivity/stats'
          },
          {
            name: 'Anomalies',
            icon: faClipboardList,
            path: '/cati/anomalies'
          },
          {
            name: 'Blacklist Agences',
            icon: faBan,
            path: '/cati/blacklist'
          }
        ],
        show: true
      });

      // Enquêteurs CATI Section
      baseItems.push({
        name: 'Enquêteurs CATI',
        icon: faUsers,
        type: 'dropdown',
        subItems: [
          {
            name: 'Présence',
            icon: faUserCheck,
            path: '/cati/attendance'
          },
          {
            name: 'Nouveau',
            icon: faUserPlus,
            path: '/cati/interviewer/create'
          },
          {
            name: 'Liste',
            icon: faList,
            path: '/cati/interviewers'
          },
          {
            name: 'Audit',
            icon: faClipboardList,
            path: '/cati/interviewers/audit'
          }
        ],
        show: true
      });

      // Superviseurs CATI Section (resp_cati manages supervisor_cati users) — temporarily hidden
      // baseItems.push({
      //   name: 'Superviseurs CATI',
      //   icon: faUsers,
      //   type: 'dropdown',
      //   subItems: [
      //     {
      //       name: 'Liste',
      //       icon: faList,
      //       path: '/supervisors-cati'
      //     },
      //     {
      //       name: 'Nouveau',
      //       icon: faUserPlus,
      //       path: '/supervisors-cati/create'
      //     }
      //   ],
      //   show: true
      // });

      // Enquêtes CATI Section
      baseItems.push({
        name: 'Enquêtes',
        icon: faFileText,
        type: 'dropdown',
        subItems: [
          {
            name: 'Nouvelle',
            icon: faPlus,
            path: '/cati/surveys/create'
          },
          {
            name: 'Liste',
            icon: faList,
            path: '/cati/surveys'
          }
        ],
        show: true
      });

      // CATI Configuration Section
      baseItems.push({
        name: 'Configuration CATI',
        icon: faCog,
        type: 'dropdown',
        subItems: [
          {
            name: 'Posts',
            icon: faList,
            path: '/cati/posts'
          },
          {
            name: 'Assignations',
            icon: faClipboardCheck,
            path: '/cati/assignments'
          }
        ],
        show: true
      });

      // Statistiques Contrôle (CATI surveys only)
      baseItems.push({
        name: 'Statistiques Contrôle',
        icon: faChartPie,
        path: '/control/stats',
        show: true
      });
      // Formations & Briefs for resp_cati
      baseItems.push({
        name: 'Formations & Briefs',
        icon: faBullhorn,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Liste',
            icon: faList,
            path: '/formations',
            show: true
          },
          {
            name: 'Nouvelle',
            icon: faPlus,
            path: '/formations/create',
            show: true
          }
        ]
      });

    }

    // Supervisor CATI — read-only deputy under resp_cati.
    // Same CATI operational surface minus: all payment pages, Configuration CATI,
    // 'Nouveau' entries (interviewers, surveys). Full access to affectations,
    // blacklist, anomalies refresh. View-only for attendance, surveys, interviewers.
    if (user?.role === 'supervisor_cati') {
      baseItems.push({
        name: 'Opérations CATI',
        icon: faPhone,
        type: 'dropdown',
        subItems: [
          {
            name: 'Affectations CATI',
            icon: faFileText,
            path: '/cati/interview-uploads'
          },
          {
            name: 'Productivité',
            icon: faChartLine,
            path: '/cati/productivity'
          },
          {
            name: 'Statistiques Productivité',
            icon: faChartBar,
            path: '/cati/productivity/stats'
          },
          {
            name: 'Anomalies',
            icon: faClipboardList,
            path: '/cati/anomalies'
          },
          {
            name: 'Blacklist Agences',
            icon: faBan,
            path: '/cati/blacklist'
          }
        ],
        show: true
      });

      baseItems.push({
        name: 'Enquêteurs CATI',
        icon: faUsers,
        type: 'dropdown',
        subItems: [
          {
            name: 'Présence',
            icon: faUserCheck,
            path: '/cati/attendance'
          },
          {
            name: 'Liste',
            icon: faList,
            path: '/cati/interviewers'
          }
        ],
        show: true
      });

      baseItems.push({
        name: 'Enquêtes',
        icon: faFileText,
        path: '/cati/surveys',
        show: true
      });

      baseItems.push({
        name: 'Statistiques Contrôle',
        icon: faChartPie,
        path: '/control/stats',
        show: true
      });
    }

    if (user?.role === 'superviseur') {
      // Formations for superviseur
      baseItems.push({
        name: 'Mes Formations & Briefs',
        icon: faBullhorn,
        path: '/my-formations',
        show: true
      });
      // Quotas for superviseur
      baseItems.push({
        name: 'Mes Quotas',
        icon: faClipboardList,
        path: '/supervisor/my-quotas',
        show: true
      });
      // Location addresses map — placed right under Mes Quotas
      baseItems.push({
        name: 'Carte des adresses',
        icon: faMapLocationDot,
        path: '/supervisor/addresses-map',
        show: true
      });
      // Payments for superviseur
      baseItems.push({
        name: 'Mes Paiements',
        icon: faMoneyBillWave,
        path: '/f2f/my-payments',
        show: true
      });
      // Frais de déplacement for superviseur
      baseItems.push({
        name: 'Frais de Déplacement',
        icon: faRoute,
        path: '/supervisor/frais-deplacement',
        show: true
      });
      // Enquêteurs section (dropdown) for superviseur
      baseItems.push({
        name: 'Enquêteurs',
        icon: faUsers,
        type: 'dropdown',
        show: true,
        subItems: [
          {
            name: 'Liste',
            icon: faUsers,
            path: '/interviewers',
            show: true
          },
          {
            name: 'Nouvel Enquêteur',
            icon: faUserPlus,
            path: '/interviewers/create',
            show: true
          },
          {
            name: 'Enquêteurs Archivés',
            icon: faArchive,
            path: '/interviewers/archived',
            show: true
          }
        ]
      });
    }

    // CATI Interviewer specific navigation
    if (user?.role === 'enqueteur') {
      // Formations for enqueteur
      baseItems.push({
        name: 'Mes Formations & Briefs',
        icon: faBullhorn,
        path: '/my-formations',
        show: true
      });
      
      if (user?.is_cati) {
      baseItems.push({
        name: 'Mes Paiements',
        icon: faMoneyBillWave,
        path: '/cati/my-payments',
        show: true
      });
      baseItems.push({
        name: 'CATI',
        icon: faClipboardCheck,
        path: '/cati/interviewer/interviews',
        show: true
      });
      baseItems.push({
        name: 'Rendez-vous',
        icon: faCalendarAlt,
        path: '/cati/interviewer/rendez-vous',
        show: true
      });
      }
    }

    return baseItems.filter(item => item.show);
  };

  const navigationItems = getNavigationItems() || [];

  const isActive = (path) => {
    if (!path) return false; // Handle undefined/null paths
    if (path.includes('?')) {
      const [basePath] = path.split('?');
      return location.pathname === '/profile';
    }
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 m-2.5">

        {/* Mobile Overlay */}
        {isMobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
            onClick={toggleMobileMenu}
          ></div>
        )}

        {/* Top Navbar */}
        <div className="flex fixed top-2.5 left-2.5 right-2.5 z-50">
          {/* Hamburger */}
          <div 
            className="w-16 h-15 bg-red-600 p-4 rounded-tl-2xl cursor-pointer" 
            onClick={() => {
              if (window.innerWidth < 1024) {
                toggleMobileMenu();
              } else {
                toggleSidebar();
              }
            }}
          >
            <div className="space-y-1">
              <div className="w-8 h-1 bg-red-200 rounded"></div>
              <div className="w-8 h-1 bg-red-200 rounded"></div>
              <div className="w-8 h-1 bg-red-200 rounded"></div>
            </div>
          </div>
          
          {/* Top Menu */}
          <div className="flex-1 h-15 bg-white rounded-tr-2xl flex justify-between items-center px-3 sm:px-5 shadow-sm">
            <Link to="/" className="text-red-600 text-lg sm:text-xl font-bold tracking-widest cursor-pointer">
              IMMERSION
            </Link>
            <ul className="flex space-x-1 sm:space-x-2 items-center">
              <li>
                <NotificationBell />
              </li>
              <li>
                <button 
                  onClick={() => navigate('/profile')}
                  className="w-8 h-8 sm:w-9 sm:h-9 border border-red-600 rounded-full flex items-center justify-center text-red-600 hover:bg-red-600 hover:text-white transition-colors"
                >
                  <FontAwesomeIcon icon={faUser} className="text-xs sm:text-sm" />
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Sidebar */}
        <div className={`
          fixed top-16 left-2.5 bg-red-600 rounded-bl-2xl transition-all duration-300 ease-in-out z-50
          flex-col
          ${isMobileMenuOpen ? 'w-64' : (isCollapsed ? 'w-16' : 'w-64')}
          h-[calc(100vh-5rem)]
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isMobileMenuOpen ? 'flex' : 'hidden lg:flex'}
          overflow-hidden
        `}>
          {/* Navigation Menu */}
          <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden sidebar-scroll pt-4 pb-4">
            <ul className="space-y-0">
               {(navigationItems || []).map((item, index) => {
                 if (!item) return null;
                 
                 return (
                   <li key={index}>
                     {item.type === 'dropdown' ? (
                    // Dropdown Item
                    <div>
                      <button
                        onClick={() => toggleDropdown(item.name)}
                        className={`
                          w-full block px-5 py-5 text-red-200 hover:text-white hover:bg-red-700 
                          transition-all duration-300 ease-in-out relative group text-left
                          ${isCollapsed && !isMobileMenuOpen ? 'text-center' : ''}
                        `}
                      >
                        <span className="flex items-center">
                          <span className="flex items-center justify-center w-5 h-5">
                            <FontAwesomeIcon icon={item.icon} className="text-lg" />
                          </span>
                          <span className={`ml-3 font-medium whitespace-nowrap transition-all duration-300 ease-in-out ${
                            (!isCollapsed || isMobileMenuOpen) ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
                          }`}>
                            {item.name}
                          </span>
                          <FontAwesomeIcon 
                            icon={openDropdowns[item.name] ? faChevronDown : faChevronRight} 
                            className={`ml-auto text-sm transition-all duration-500 ease-in-out ${
                              (!isCollapsed || isMobileMenuOpen) ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-1'
                            }`}
                            style={{
                              transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1), transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                          />
                        </span>
                      </button>
                      
                      {/* Dropdown Items */}
                      <div 
                        className={`overflow-hidden ${
                          openDropdowns[item.name] ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                        style={{
                          transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      >
                         <ul className="bg-red-700/50">
                           {(item.subItems || []).map((subItem, subIndex) => {
                             if (!subItem || !subItem.path || !subItem.name) return null;
                             
                             return (
                               <li key={subIndex}>
                                 <Link
                                   to={subItem.path}
                                   className={`
                                     block py-3 text-red-200 hover:text-white hover:bg-red-700 
                                     transition-all duration-300 ease-in-out relative group text-sm
                                     ${(!isCollapsed || isMobileMenuOpen) ? 'px-5 pl-12' : 'px-3 pl-8'}
                                     ${isActive(subItem.path) ? 'bg-red-700 text-white' : ''}
                                   `}
                                   onClick={() => setIsMobileMenuOpen(false)}
                                 >
                                   {isActive(subItem.path) && (
                                     <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-200"></div>
                                   )}
                                   <span className="flex items-center">
                                     <span className="flex items-center justify-center w-4 h-4">
                                       <FontAwesomeIcon icon={subItem.icon} className="text-sm" />
                                     </span>
                                     <span className={`font-medium whitespace-nowrap transition-all duration-300 ease-in-out ${
                                       (!isCollapsed || isMobileMenuOpen) ? 'ml-3 opacity-100' : 'ml-2 opacity-0'
                                     }`}>
                                       {subItem.name}
                                     </span>
                                   </span>
                                 </Link>
                               </li>
                             );
                           })}
                        </ul>
                      </div>
                    </div>
                  ) : (
                    // Regular Link Item
                    <Link
                      to={item.path}
                      className={`
                        block px-5 py-5 text-red-200 hover:text-white hover:bg-red-700 
                        transition-all duration-200 relative group
                        ${isActive(item.path) ? 'bg-red-700 text-white' : ''}
                        ${isCollapsed && !isMobileMenuOpen ? 'text-center' : ''}
                      `}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {isActive(item.path) && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-200"></div>
                      )}
                      <span className="flex items-center">
                        <span className="flex items-center justify-center w-5 h-5">
                          <FontAwesomeIcon icon={item.icon} className="text-lg" />
                        </span>
                        <span className={`ml-3 font-medium whitespace-nowrap transition-all duration-300 ease-in-out ${
                          (!isCollapsed || isMobileMenuOpen) ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
                        }`}>
                          {item.name}
                        </span>
                      </span>
                     </Link>
                   )}
                   </li>
                 );
               })}
            </ul>
          </nav>

          {/* User Info & Logout */}
          <div className={`shrink-0 p-4 border-t border-red-700 bg-red-600 transition-all duration-300 ease-in-out ${
            (!isCollapsed || isMobileMenuOpen) ? 'opacity-100' : 'opacity-0 pointer-events-none h-0 p-0 overflow-hidden'
          }`}>
            <div 
              onClick={() => navigate('/profile')}
              className="flex items-center mb-4 transition-all duration-300 ease-in-out cursor-pointer hover:bg-red-700 rounded-lg p-2 -m-2"
            >
              <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-3">
                <FontAwesomeIcon icon={faUser} className="text-white text-sm" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user?.name}</p>
                <p className="text-red-200 text-xs truncate">
                  {user?.role_display || user?.role}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-2 text-red-200 hover:text-white hover:bg-red-700 transition-all duration-200 rounded-lg group"
            >
              <FontAwesomeIcon icon={faSignOutAlt} className="text-lg" />
              <span className="ml-3 font-medium">
                Déconnexion
              </span>
            </button>
          </div>

          {/* Collapsed Logout Button */}
          {isCollapsed && !isMobileMenuOpen && (
            <div className="absolute bottom-4 left-0 right-0 flex justify-center">
              <button
                onClick={handleLogout}
                className="w-10 h-10 flex items-center justify-center text-red-200 hover:text-white hover:bg-red-700 transition-all duration-200 rounded-lg group"
                title="Déconnexion"
              >
                <FontAwesomeIcon icon={faSignOutAlt} className="text-lg" />
              </button>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className={`
          transition-all duration-300 ease-in-out mt-16
          ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}
        `}>
          {/* Page Content */}
          <div className="p-3 sm:p-4">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SideBar;
